import { MapPin, Phone, User, Package, Truck } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { ApplyTypeBadge, RefundStatusBadge } from '../../components/RefundStatusBadge'
import { REFUND_STATUS, type Refund } from '@/schemas/supplier'

interface RefundDetailDrawerProps {
  refund: Refund | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirmReceive?: (refundId: number) => void
  isConfirmReceiveLoading?: boolean
}

export function RefundDetailDrawer({
  refund,
  open,
  onOpenChange,
  onConfirmReceive,
  isConfirmReceiveLoading,
}: RefundDetailDrawerProps) {
  // 是否显示确认收货按钮（已退货状态）
  const canConfirmReceive = refund && refund.refund_type === REFUND_STATUS.RETURNED

  // 处理确认收货
  const handleConfirmReceive = () => {
    if (onConfirmReceive && refund) {
      onConfirmReceive(refund.id)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>退款详情</SheetTitle>
          {refund && (
            <SheetDescription>订单号：{refund.order_id}</SheetDescription>
          )}
        </SheetHeader>

        {!refund ? (
          <div className="space-y-6 py-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        ) : (
          <>
            {/* 操作按钮 */}
            {canConfirmReceive && (
              <div className="py-4">
                <Button
                  className="w-full"
                  onClick={handleConfirmReceive}
                  disabled={isConfirmReceiveLoading}
                >
                  <Package className="mr-1.5 h-4 w-4" />
                  {isConfirmReceiveLoading ? '处理中...' : '确认收货'}
                </Button>
              </div>
            )}

            {/* 状态概览 */}
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm text-muted-foreground">退款状态</span>
              <RefundStatusBadge refundStatus={refund.refund_type} />
            </div>

            <ScrollArea className="h-[calc(100vh-220px)]">
              <div className="space-y-4 mt-4">
                {/* 基本信息 */}
                <div className="rounded-lg border bg-card p-4">
                  <h3 className="font-medium mb-3">基本信息</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">订单号：</span>
                      <span className="font-mono">{refund.order_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">申请类型：</span>
                      <ApplyTypeBadge applyType={refund.apply_type} />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">退款金额：</span>
                      <span className="text-red-500 font-medium">
                        ¥{(refund.refund_price ?? 0).toFixed(2)}
                      </span>
                    </div>
                    {refund.refund_reason && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">退款原因：</span>
                        <span>{refund.refund_reason}</span>
                      </div>
                    )}
                    {refund.refund_explain && (
                      <div>
                        <span className="text-muted-foreground">退款说明：</span>
                        <p className="mt-1">{refund.refund_explain}</p>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">申请时间：</span>
                      <span>{refund.add_time}</span>
                    </div>
                    {refund.refund_time && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">退款时间：</span>
                        <span>{refund.refund_time}</span>
                      </div>
                    )}
                    {refund.refuse_reason && (
                      <div>
                        <span className="text-muted-foreground text-red-500">拒绝原因：</span>
                        <p className="mt-1 text-red-500">{refund.refuse_reason}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 收货信息 */}
                {refund.order_info && (
                  <div className="rounded-lg border bg-card p-4">
                    <h3 className="font-medium flex items-center gap-2 mb-3">
                      <MapPin className="h-4 w-4" />
                      收货信息
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{refund.order_info.real_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{refund.order_info.user_phone}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 物流信息 */}
                {refund.delivery_name && (
                  <div className="rounded-lg border bg-card p-4">
                    <h3 className="font-medium flex items-center gap-2 mb-3">
                      <Truck className="h-4 w-4" />
                      物流信息
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">快递公司：</span>
                        <span>{refund.delivery_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">运单号：</span>
                        <span className="font-mono">{refund.delivery_id}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 商品信息 */}
                {refund.cart_info && refund.cart_info.length > 0 && (
                  <div className="rounded-lg border bg-card p-4">
                    <h3 className="font-medium flex items-center gap-2 mb-4">
                      <Package className="h-4 w-4" />
                      商品列表
                    </h3>
                    <div className="space-y-4">
                      {refund.cart_info.map((item, index) => (
                        <div key={item.cart_id || index}>
                          {index > 0 && <Separator className="mb-4" />}
                          <div className="flex items-start gap-3">
                            <img
                              src={item.product_image}
                              alt={item.product_name}
                              className="w-16 h-16 object-cover rounded border"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{item.product_name}</p>
                              {item.product_attr && (
                                <p className="text-xs text-muted-foreground mt-0.5">{item.product_attr}</p>
                              )}
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-sm">¥{(item.price ?? 0).toFixed(2)}</span>
                                <span className="text-sm text-muted-foreground">x{item.cart_num ?? 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Separator className="my-4" />
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">商品总数：</span>
                        <span>
                          {refund.cart_info.reduce((sum, item) => sum + (item.cart_num ?? 0), 0)} 件
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">商品总价：</span>
                        <span>
                          ¥{refund.cart_info.reduce((sum, item) => sum + (item.total_price ?? 0), 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between font-medium text-base pt-2 border-t">
                        <span>退款金额：</span>
                        <span className="text-red-500">¥{(refund.refund_price ?? 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
